import {
  AHRadioButton,
  AHSeparator,
  Box,
  Input,
  ListItem,
  SectionHeader,
  useBoxStyle,
  useTheme,
} from "arkh";
import { useIntl } from "react-intl";

import React, { useCallback, useLayoutEffect, useState } from "react";
import { useGetRoleDescription, useGetRoleLabels } from "./RoleLabels";

import { validateEmail } from "../../common/validateEmail";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useAlert } from "../../common/Alert";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { AccountEditAccountOwnerCard } from "../AccountEdit/AccountEditAccountOwnerCard";
import { SafeAreaProvider } from "react-native-safe-area-context";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { CreateAccountQuery } from "./__generated__/CreateAccountQuery.graphql";
import { CreateAccountMutation } from "./__generated__/CreateAccountMutation.graphql";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

const InnerCreateAccountScreen = (
  props: {
    fetchKey: string;
  } & CreateAccountScreenProps
) => {
  const data = useLazyLoadQuery<CreateAccountQuery>(
    graphql`
      query CreateAccountQuery($employeeId: ID, $storeId: ID) {
        store(id: $storeId) {
          employee(id: $employeeId) {
            ...AccountEditAccountOwnerCard_employee
          }
        }
      }
    `,
    {
      employeeId: props.route.params.employeeId,
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
    }
  );
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const [role, setRole] = useState<"CO_WORKER" | "ADMIN" | "MANAGER">(
    "CO_WORKER"
  );

  const [email, setEmail] = useState("");
  const hasValidEmail = validateEmail(email);
  const [inviteUserMutation, loading] = useMutation<CreateAccountMutation>(
    graphql`
      mutation CreateAccountMutation(
        $email: String!
        $roles: [Roles!]!
        $employeeId: ID
        $storeId: ID
      ) {
        store(id: $storeId) {
          inviteUser(email: $email, roles: $roles, employeeId: $employeeId) {
            id
            employee {
              id
            }
            users {
              id
            }
          }
        }
      }
    `
  );
  const Alert = useAlert();
  const inviteUser = useCallback(() => {
    inviteUserMutation({
      variables: {
        email,
        roles:
          props.route.params.type === "GROUP" ? ["COWORKER_GROUP"] : [role],
        employeeId: props.route.params.employeeId,
        storeId: props.route.params.storeId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
      onError: () => {
        Alert.error(
          intl.formatMessage({
            description: "invite user error alert title",
            defaultMessage: "Failed to invite user",
          })
        );
      },
    });
  }, [
    inviteUserMutation,
    email,
    props.route.params.type,
    props.route.params.employeeId,
    props.navigation,
    role,
    Alert,
    intl,
    props.route.params.storeId,
  ]);
  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions(
      getNavigationHeaderOptions({
        type: "CREATE",
        disabled: !hasValidEmail,
        title:
          props.route.params.type === "INDIVIDUAL"
            ? intl_tokens.CreatePersonalAccount
            : intl_tokens.CreateGroupAccount,
        onSave() {
          inviteUser();
        },
        saveLoading: loading,
        onCancel() {
          props.navigation.goBack();
        },
      })
    );
  }, [
    props,
    loading,
    inviteUser,
    getNavigationHeaderOptions,
    hasValidEmail,
    intl_tokens.CreatePersonalAccount,
    intl_tokens.CreateGroupAccount,
  ]);

  const getRoleLabels = useGetRoleLabels();
  const getRoleDescriptions = useGetRoleDescription();
  const theme = useTheme();
  const contentContainerStyle = useBoxStyle({
    spaceBottom: "medium",
  });

  return (
    <KeyboardAwareScrollView
      style={{ flex: 1, backgroundColor: theme["surface-background-light"] }}
      contentContainerStyle={contentContainerStyle}
      automaticallyAdjustsScrollIndicatorInsets
    >
      {data.store?.employee ? (
        <AccountEditAccountOwnerCard employee={data.store.employee} />
      ) : null}
      <Box spaceHorizontal="medium" spaceTop="medium">
        <SectionHeader new>
          <SectionHeader.Title>{intl_tokens.Email}</SectionHeader.Title>
        </SectionHeader>
        <Input
          placeholder={intl_tokens.Email}
          value={email}
          autoFocus={props.route.params.type === "GROUP"}
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          keyboardType="email-address"
          returnKeyType="send"
          returnKeyLabel="create"
          onChangeText={(val) => setEmail(val)}
          onSubmitEditing={() => {
            if (hasValidEmail) {
              inviteUser();
            }
          }}
        />

        {props.route.params.type === "GROUP" ? null : (
          <Box spaceTop="medium">
            <SectionHeader new>
              <SectionHeader.Title>{intl_tokens.Permissions}</SectionHeader.Title>
            </SectionHeader>

            {(["CO_WORKER", "MANAGER", "ADMIN"] as const).map(
              (_role, index) => {
                return (
                  <React.Fragment key={_role}>
                    {index !== 0 ? <AHSeparator /> : null}
                    <ListItem
                      border="border-primary"
                      isFirst={index === 0}
                      isLast={index === 2}
                      onPress={() => setRole(_role)}
                      left={<AHRadioButton selected={role === _role} />}
                    >
                      <ListItem.Title>{getRoleLabels(_role)}</ListItem.Title>
                      <ListItem.SubTitle>
                        {getRoleDescriptions(_role)}
                      </ListItem.SubTitle>
                    </ListItem>
                  </React.Fragment>
                );
              }
            )}
          </Box>
        )}
      </Box>
    </KeyboardAwareScrollView>
  );
};

type ParamList = {
  CreateAccountScreen: {
    type: "INDIVIDUAL" | "GROUP";
    employeeId?: string;
    storeId?: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

type CreateAccountScreenProps = {
  route: RouteProp<ParamList, "CreateAccountScreen">;
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"CreateAccount">,
    NativeStackNavigationProp<ParamList, "CreateAccountScreen">
  >;
};

const CreateAccountScreen = (props: CreateAccountScreenProps) => {
  return (
    <SafeAreaProvider>
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <InnerCreateAccountScreen fetchKey={fetchKey} {...props} />
        )}
      </LazyQueryNetworkBoundrary>
    </SafeAreaProvider>
  );
};

export const CreateAccount = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CreateAccount">) => {
    const getNavigationHeaderOptions = useNavigationHeaderOptions();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="CreateAccountScreen"
          component={CreateAccountScreen}
          initialParams={{
            type: props.route.params.type,
            employeeId: props.route.params.employeeId,
            storeId: props.route.params.storeId,
          }}
          options={getNavigationHeaderOptions({
            type: "BACK",
            onBack() {
              props.navigation.goBack();
            },
          })}
        />
      </Stack.Navigator>
    );
  }
);
